import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { getBuyerPitchPaymentStatus } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import NavHeader from '../components/NavHeader';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';

const BuyerPitchPaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    try {
      const result = await getBuyerPitchPaymentStatus(sessionId);
      setPaymentInfo(result);
      if (result.payment_status === 'paid') {
        setStatus('success');
      } else {
        setStatus('pending');
      }
    } catch (err) {
      console.error('Error checking payment:', err);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <NavHeader />
      
      <section className="max-w-2xl mx-auto px-6 py-24">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardContent className="p-8 text-center">
            {status === 'loading' && (
              <>
                <Loader2 className="w-16 h-16 text-blue-400 mx-auto mb-6 animate-spin" />
                <h1 className="text-2xl font-bold text-white mb-4">Confirming Payment...</h1>
                <p className="text-slate-400">Please wait while we verify your payment.</p>
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-6" />
                <h1 className="text-2xl font-bold text-white mb-4">Payment Successful!</h1>
                <p className="text-slate-400 mb-6">
                  Your spot has been secured. We'll be in touch with details about your pitch session.
                </p>
                {paymentInfo && (
                  <p className="text-slate-500 text-sm mb-6">
                    Amount paid: ${paymentInfo.amount} {paymentInfo.currency?.toUpperCase()}
                  </p>
                )}
                <Link to="/buyer-pitches">
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    Back to Sessions
                  </Button>
                </Link>
              </>
            )}

            {status === 'pending' && (
              <>
                <Loader2 className="w-16 h-16 text-amber-400 mx-auto mb-6" />
                <h1 className="text-2xl font-bold text-white mb-4">Payment Processing</h1>
                <p className="text-slate-400 mb-6">
                  Your payment is being processed. You'll receive a confirmation email once complete.
                </p>
                <Link to="/buyer-pitches">
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    Back to Sessions
                  </Button>
                </Link>
              </>
            )}

            {status === 'error' && (
              <>
                <XCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
                <h1 className="text-2xl font-bold text-white mb-4">Something Went Wrong</h1>
                <p className="text-slate-400 mb-6">
                  We couldn't verify your payment. Please contact support if you believe this is an error.
                </p>
                <Link to="/buyer-pitches">
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    Back to Sessions
                  </Button>
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      </section>

      <footer className="bg-slate-950 text-slate-400 py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm">&copy; {new Date().getFullYear()} The Founder Initiative. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BuyerPitchPaymentSuccess;
